import React, {useState} from "react";
import moment from "moment";
import {Box, ButtonBase, Menu, MenuItem, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowDown01Icon, Refresh01Icon, Tick02Icon} from "@hugeicons/core-free-icons";

export const REFRESH_INTERVAL_PRESETS = [
  {label: "Off", value: null},
  {label: "10s", value: 10000},
  {label: "30s", value: 30000},
  {label: "1 min", value: 60000},
  {label: "5 min", value: 300000},
];

export const DEFAULT_REFRESH_INTERVAL = 60000;

const ACCENT = "#8491F7";

// How often the dashboard refetches its statistics
const RefreshIntervalSelect = ({value, onChange, lastFetched}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const current = REFRESH_INTERVAL_PRESETS.find(preset => preset.value === value) || REFRESH_INTERVAL_PRESETS[0];

  return (
    <>
      {/* small text control, no background or border */}
      <ButtonBase onClick={(e) => setAnchorEl(e.currentTarget)} aria-label={"Select refresh interval"} sx={{
        gap: 0.5, px: 0.75, height: 26, borderRadius: "8px", fontSize: 12, fontWeight: 500, color: "#8A8AA0",
        "&:hover": {color: "#23264D", bgcolor: "#F4F4FA"},
      }}>
        <HugeiconsIcon icon={Refresh01Icon} size={13}/>
        {value ? `Every ${current.label}` : "Auto refresh off"}
        <HugeiconsIcon icon={ArrowDown01Icon} size={12}/>
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
        transformOrigin={{vertical: "top", horizontal: "right"}}
        PaperProps={{sx: {borderRadius: "12px", mt: 0.5, minWidth: 180}}}
      >
        {lastFetched && (
          <Typography sx={{px: 2, py: 0.75, fontSize: 11, color: "#8A8AA0"}}>
            Updated {moment(lastFetched).format("HH:mm:ss")}
          </Typography>
        )}
        {REFRESH_INTERVAL_PRESETS.map(preset => (
          <MenuItem
            key={preset.label}
            dense
            selected={preset.value === value}
            onClick={() => {
              onChange(preset.value);
              setAnchorEl(null);
            }}
            sx={{justifyContent: "space-between"}}
          >
            {preset.label}
            {preset.value === value && <HugeiconsIcon icon={Tick02Icon} size={16}/>}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// Compact page heading: title and one-line description on the left; refresh interval and the filters
// (actions, e.g. language and version type) in one row on the right
export const DashboardHeading = ({
                                   title, refreshInterval, onChangeRefreshInterval,
                                   lastFetched, actions
                                 }) => (
  <Stack
    component={"section"}
    direction={{xs: "column", md: "row"}}
    justifyContent={"space-between"}
    alignItems={{xs: "flex-start", md: "center"}}
    gap={1.5}
  >
    <Box sx={{minWidth: 0}}>
      <Stack direction={"row"} alignItems={"center"} gap={1.25}
             sx={{fontSize: 10, fontWeight: 600, letterSpacing: "0.14em", color: ACCENT}}>
        <Box component={"span"} sx={{width: 12, height: 2, borderRadius: 1, bgcolor: ACCENT}}/>
        YOUR DASHBOARD
      </Stack>
      <Typography component={"h1"} sx={{
        fontSize: {xs: 20, md: 24}, fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.2, color: "#23264D",
      }}>
        {title}<Box component={"span"} sx={{color: ACCENT}}>.</Box>
      </Typography>
      <Typography sx={{mt: 0.25, fontSize: 12, color: "#8A8AA0"}}>
        A clear view of every conversation, response, and outcome.
      </Typography>
    </Box>
    <Stack direction={"column"} justifyContent={"flex-end"} alignItems={"flex-end"}>
      <RefreshIntervalSelect value={refreshInterval} onChange={onChangeRefreshInterval} lastFetched={lastFetched}/>
      <Stack direction={"row"} alignItems={"center"} justifyContent={{md: "flex-end"}} gap={0.75} flexWrap={"wrap"}>
        {actions}
      </Stack>
    </Stack>
  </Stack>
);
